import { createTheme, Theme, ThemeOptions, useTheme } from '@mui/material/styles';
import { muiStyleSheet } from '../util/Styles';
import { MindTheme } from './MindTheme';

/**
 * Game Board Theme
 * Colors and styling for the Family Feud-style game board
 */

/**
 * Gradient color pairs for game UI elements
 */
export type GradientColors = {
	start: string;
	end: string;
};

/**
 * Complete game palette including colors and gradients
 */
export type GamePalette = {
	gold: string;
	darkGold: string;
	white: string;
	green: string;
	darkGreen: string;
	lightGray: string;
	darkGray: string;
	textDark: string;
	red: string;
	teamA: string;
	teamB: string;
	strikeActive: string;
	strikeInactive: string;
	answerHidden: GradientColors;
	answerRevealed: GradientColors;
	answerRevealedDark: string;
	boardBackground: GradientColors;
	logoBackground: string;
	answerBoard: GradientColors;
	scoreBox: GradientColors;
};

/**
 * Game board colour palette with all colors and gradients
 */
export const gamePalette: GamePalette = {
	gold: '#ffd700',
	darkGold: '#ffb700',
	white: '#FFFFFF',
	green: '#4caf50',
	darkGreen: '#357a38',
	lightGray: '#f5f5f5',
	darkGray: '#333',
	textDark: '#333',
	red: '#d32f2f',
	// Team colors for Face Off mode
	teamA: '#4a90e2',
	teamB: '#d32f2f',
	// Strike indicator
	strikeActive: '#d32f2f',
	strikeInactive: '#999999',
	// Answer card gradients
	answerHidden: {
		start: '#5b2c91',
		end: '#3d1d61',
	},
	answerRevealed: {
		start: '#4a90e2',
		end: '#357abd',
	},
	answerRevealedDark: '#2e5f8f',
	// Background gradients
	boardBackground: {
		start: '#4caf50',
		end: '#357a38',
	},
	logoBackground: '#100D24',
	answerBoard: {
		start: '#ffd700',
		end: '#ffb700',
	},
	scoreBox: {
		start: '#ffd700',
		end: '#ffb700',
	},
};

// Augment MUI's palette so custom game theme properties are typed and usable in sx props.
declare module '@mui/material/styles' {
	// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
	interface Palette {
		game: GamePalette;
	}
	// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
	interface PaletteOptions {
		game?: GamePalette;
	}
}

const themeOptions: ThemeOptions = {
	palette: {
		mode: 'light',
		game: gamePalette,
	},
};

export const GameTheme = createTheme(MindTheme, themeOptions);

/**
 * Custom hook to get the theme with proper game palette typing
 * Use this in game components instead of the standard useTheme hook
 * to get full type safety for theme.palette.game properties
 */
export function useGameTheme(): Theme {
	return useTheme();
}

export const gameStyles = muiStyleSheet({
	boardBackground: (theme) => ({
		background: `linear-gradient(180deg, ${theme.palette.game.boardBackground.start} 0%, ${theme.palette.game.boardBackground.end} 100%)`,
	}),
});